import { z } from 'zod'
import { AgentIdSchema } from './agent.ts'
import { ACK_TEXT_MAX, ackRecordFields } from './consent.ts'
import { GroupRefSchema, OwnerRefSchema, OwnerViewSchema } from './groups.ts'
import { MessageParamsSchema } from './messages/kinds.ts'
import {
  AudienceInputSchema,
  AudienceViewSchema,
  SharingClosedReasonSchema,
  SharingRequestStatusSchema
} from './sharing.ts'

/**
 * The marketplace surface (PRD-18, phases 1-2).
 *
 * Everything here is the EXPERIENCE plane: what a consumer or maker sees and says. The
 * policy plane (models, retrieval tuning, budgets, approval rules) deliberately has no
 * representation in these shapes — a maker request that tries to carry one is rejected by
 * the strict schemas, which is half of AGM-021's enforcement (the server's field-mapping
 * is the other half).
 */

export const ListingStateSchema = z.enum(['draft', 'published', 'archived', 'suspended'])
export type ListingState = z.infer<typeof ListingStateSchema>

/**
 * Accent tokens, not colors: the web app maps each to a CSS variable pair that keeps AA
 * contrast in both themes. A hex here would let a listing paint outside the design system.
 * `amber` is deliberately absent — that channel means "live" in the shell.
 */
export const AgentAccentSchema = z.enum(['slate', 'teal', 'plum', 'moss', 'navy', 'clay'])
export type AgentAccent = z.infer<typeof AgentAccentSchema>

/** One or two glyphs (an emoji, or initials rendered as a monogram). */
export const AgentIconSchema = z.string().max(8)

export const StarterSchema = z.string().min(1).max(200)

/**
 * What one reader is to one listing (PRD-22 D-7).
 *
 * A role rather than a `canEdit` boolean: a boolean is one screen's opinion frozen into a
 * contract, and the next screen needs a different one. It is also not a disclosure — it tells
 * the reader something about themselves, which they already knew — so it rides on the consumer
 * payload without widening what that payload says about the agent.
 *
 * `manager` is a manager of the group that owns the agent (PRD-26 D-19): they maintain it as a
 * personal owner does. A stake outranks the platform role deliberately: an administrator who also
 * owns the listing reads as `owner`, because the rights they exercise there are the owner's and
 * the surfaces say so.
 */
export const ListingViewerRoleSchema = z.enum(['owner', 'manager', 'editor', 'invited', 'admin', 'viewer'])
export type ListingViewerRole = z.infer<typeof ListingViewerRoleSchema>

export const ListingHealthSchema = z.object({
  status: z.enum(['ok', 'attention']),
  issues: z.array(
    z.object({
      code: z.enum([
        'knowledge_missing',
        'capability_disabled',
        'capability_quarantined',
        'layer_dropped',
        'coverage_gap',
        'owner_deactivated'
      ]),
      /** Plain remediation copy, written for the owner — never shown to consumers. */
      message: z.string(),
      /**
       * Which sentence this is, so the owner reads it in their own language (PRD-27 D-4).
       *
       * `code` cannot do this job any more than `ApiIssue.code` could. Half of the six codes
       * cover several distinct findings — `capability_disabled` alone is *withdrawn by the
       * system it came from*, *switched off by an administrator* and *no longer exists at
       * all*, three different things to go and do — and keying off the code would collapse
       * them into one sentence, which is the F-1 mistake one level down.
       *
       * Nullish because this is a PERSISTED record: `agent_listings.health` holds snapshots
       * written before this field existed, and a recomputation is up to five minutes away.
       * Those render from `message`, exactly as they always did. Same bargain as
       * `ApiIssue.messageKey`, made for the same reason (D-8).
       */
      messageKey: z.string().nullish(),
      /** What the sentence interpolates. Values only — never a pre-built phrase (D-20). */
      params: MessageParamsSchema.nullish()
    })
  ),
  computedAt: z.iso.datetime()
})
export type ListingHealth = z.infer<typeof ListingHealthSchema>

// ---------------------------------------------------------------------------
// Consumer surface
// ---------------------------------------------------------------------------

/** A gallery card. Badges derive from the RESOLVED configuration, never free-typed (AGM-001). */
export const AgentCardSchema = z.object({
  agentId: AgentIdSchema,
  displayName: z.string(),
  tagline: z.string(),
  icon: AgentIconSchema,
  accent: AgentAccentSchema,
  category: z.string(),
  owner: OwnerViewSchema,
  official: z.boolean(),
  badges: z.object({
    knowledgeBases: z.number().int().nonnegative(),
    /** True when any granted capability can take actions (always behind approval). */
    actions: z.boolean(),
    memory: z.boolean()
  }),
  /** Present only for the owner / co-editors: drafts and health are their business alone. */
  state: ListingStateSchema.optional(),
  health: z.enum(['ok', 'attention']).optional(),
  pinned: z.boolean()
})
export type AgentCard = z.infer<typeof AgentCardSchema>

export const GallerySchema = z.object({
  featured: z.array(AgentCardSchema),
  /** Published agents whose audience names a group the reader is in (PRD-26 D-33). */
  sharedWithYourGroups: z.array(AgentCardSchema),
  /**
   * The same rail, under the name the release before PRD-26 reads (D-66). A tab that loaded the
   * old app before a deploy spreads `byYourTeam` on Home and in chat, and without it that tab
   * crashes until it is reloaded. Sent for one release only; the next one drops it together with
   * the legacy mirror writes (apps/api/drizzle-contract/README.md).
   */
  byYourTeam: z.array(AgentCardSchema).optional(),
  recent: z.array(AgentCardSchema),
  all: z.array(AgentCardSchema),
  /** The caller's own drafts/co-edited agents, shown in a separate maker rail. */
  mine: z.array(AgentCardSchema),
  categories: z.array(z.string())
})
export type Gallery = z.infer<typeof GallerySchema>

/**
 * The disclosure panel (AGM-003): a pure function of the resolved definition. Nothing in
 * here is maker prose except displayName/description/tagline/starters, which are labeled
 * as such by their position — the `disclosure` object itself is machine-generated.
 */
export const AgentAboutSchema = z.object({
  agentId: AgentIdSchema,
  displayName: z.string(),
  description: z.string(),
  tagline: z.string(),
  icon: AgentIconSchema,
  accent: AgentAccentSchema,
  category: z.string(),
  official: z.boolean(),
  owner: OwnerViewSchema,
  /** Who can use it, with its groups named — never bare ids (PRD-26 GRP-102). */
  audience: AudienceViewSchema,
  state: ListingStateSchema,
  /** Consumer-facing freshness: a date, not a version number (versions are maker/admin vocabulary). */
  updatedAt: z.iso.datetime(),
  whatsNew: z.string(),
  starters: z.array(StarterSchema),
  suspendedReason: z.string(),
  replacementAgentId: z.string().nullable(),
  disclosure: z.object({
    /** 'attached' = answers only from the named bases; 'all' = everything the reader can access. */
    knowledgeMode: z.enum(['attached', 'all']),
    knowledge: z.array(
      z.object({
        /** The base's id, carried so "Ask its managers" can address it (AGM-062). The id
         *  alone reveals nothing the adjacent name has not already disclosed. */
        baseId: z.uuid(),
        name: z.string(),
        owner: OwnerViewSchema,
        /**
         * THIS reader's coverage, computed per request (AGM-062, PRD-26):
         * `full` — they can use the base; `lent` — they cannot, but it answers through this
         * agent for everyone it reaches; `none` — this agent answers without it for them.
         */
        coverage: z.enum(['full', 'lent', 'none']),
        /**
         * Their own latest ask about this base for this agent, when `coverage` is `none` — with the
         * answer, so "Its managers said no" can say who, and why, in their words.
         */
        ask: z
          .object({
            id: z.uuid(),
            status: z.enum(['pending', 'approved', 'declined', 'withdrawn', 'closed']),
            at: z.iso.datetime(),
            decidedByName: z.string().nullable(),
            note: z.string()
          })
          .nullable(),
        /** Nobody maintains the base, so an ask goes to administrators (D-17). */
        noMaintainer: z.boolean()
      })
    ),
    capabilities: z.array(
      z.object({
        /** Admin-curated plain-language name, never the technical identifier. */
        name: z.string(),
        description: z.string(),
        wording: z.enum(['reads', 'acts']),
        /**
         * False when the connected system behind it is limited to groups this reader is not in:
         * the agent will not use it for them (PRD-26 GRP-062, "Not available to you").
         */
        available: z.boolean()
      })
    ),
    grounded: z.boolean(),
    memory: z.object({
      /** Global saved preferences apply regardless of the maker's toggle (AGM-090). */
      usesSavedPreferences: z.boolean(),
      learnsHere: z.boolean()
    })
  }),
  /** First-use notices this viewer still owes an acknowledgement for (AGM-009/010). */
  noticesDue: z.array(z.enum(['actions', 'memory'])),
  /** Whether THIS viewer may copy it (owner's copy policy + audience, AGM-028). */
  copyable: z.boolean(),
  /** What THIS reader is to this agent, so a surface can label an action honestly (DTL-001). */
  viewerRole: ListingViewerRoleSchema
})
export type AgentAbout = z.infer<typeof AgentAboutSchema>

export const NoticeAckRequestSchema = z.object({ capability: z.enum(['actions', 'memory']) })

export const CopyResultSchema = z.object({
  agentId: AgentIdSchema,
  /** Attachments the copier's own authority could not hold, listed explicitly (AGM-027/028). */
  dropped: z.array(z.object({ kind: z.enum(['knowledge', 'capability']), name: z.string() }))
})
export type CopyResult = z.infer<typeof CopyResultSchema>

// ---------------------------------------------------------------------------
// Maker surface
// ---------------------------------------------------------------------------

/**
 * What a maker reads back. `capabilities` unifies builtin abilities and connected (MCP)
 * capabilities into one list — the split is an implementation detail the maker never sees
 * (memo: "What it can do"). `id` is the grant identifier (builtin name or `server/tool`);
 * the UI shows `name`/`description` from the curated catalogue.
 */
export const MakerAgentSchema = z.object({
  agentId: AgentIdSchema,
  displayName: z.string(),
  description: z.string(),
  tagline: z.string(),
  icon: AgentIconSchema,
  accent: AgentAccentSchema,
  category: z.string(),
  starters: z.array(StarterSchema),
  instructions: z.string(),
  grounded: z.boolean(),
  remembersPreferences: z.boolean(),
  knowledgeBaseIds: z.array(z.uuid()),
  capabilityIds: z.array(z.string()),
  /** Who can use it now — what is live, which only an immediate change or an approval moves. */
  audience: AudienceViewSchema,
  state: ListingStateSchema,
  /** Optimistic-concurrency token for PUT (the '*' definition row's version). */
  definitionVersion: z.number().int(),
  publishedVersion: z.number().int().nullable(),
  health: ListingHealthSchema,
  /** A person or a group (PRD-26 D-19); a group-owned agent is maintained by its managers. */
  owner: OwnerViewSchema,
  editors: z.array(
    z.object({ userId: z.uuid(), name: z.string(), email: z.string(), status: z.enum(['invited', 'accepted']) })
  ),
  copyPolicy: z.enum(['auto', 'allowed', 'blocked']),
  /**
   * The agent's latest sharing request, whatever its status: pending shows "Waiting for review",
   * a decided or closed one shows its outcome until the maker dismisses it (PRD-26 GRP-081).
   */
  sharingRequest: z
    .object({
      id: z.uuid(),
      status: SharingRequestStatusSchema,
      requestedKind: z.enum(['groups', 'everyone']),
      groups: z.array(GroupRefSchema),
      requestedAt: z.iso.datetime(),
      requestedByName: z.string().nullable(),
      decidedAt: z.iso.datetime().nullable(),
      decidedByName: z.string().nullable(),
      decisionNote: z.string(),
      closedReason: SharingClosedReasonSchema.nullable()
    })
    .nullable(),
  updatedAt: z.iso.datetime(),
  /**
   * What THIS reader is to this agent (PRD-22 DTL-020). The builder used to re-derive it from
   * `ownerId`, `editors` and the session's roles, which is how the client came to disagree
   * with `requireMakerAccess` about administrators. One answer, computed where the rule lives.
   */
  viewerRole: ListingViewerRoleSchema
})
export type MakerAgent = z.infer<typeof MakerAgentSchema>

/**
 * The ONLY fields a maker may write (AGM-020/021). `.strict()` so a forged request
 * carrying `budget`, `modelAlias`, `retrieval`, `humanInLoop`, `official` or anything else
 * fails validation instead of reaching the mapping code.
 */
export const MakerAgentUpsertSchema = z
  .object({
    displayName: z.string().min(1).max(120),
    description: z.string().max(2000).default(''),
    tagline: z.string().max(80).default(''),
    icon: AgentIconSchema.default(''),
    accent: AgentAccentSchema.default('slate'),
    category: z.string().max(60).default(''),
    starters: z.array(StarterSchema).max(4).default([]),
    instructions: z.string().max(24_000).default(''),
    grounded: z.boolean().default(false),
    remembersPreferences: z.boolean().default(false),
    knowledgeBaseIds: z.array(z.uuid()).max(20).default([]),
    capabilityIds: z.array(z.string().min(1).max(160)).max(40).default([]),
    copyPolicy: z.enum(['auto', 'allowed', 'blocked']).default('auto'),
    changeNote: z.string().max(500).default('')
  })
  .strict()
export type MakerAgentUpsert = z.infer<typeof MakerAgentUpsertSchema>

export const CreateMakerAgentRequestSchema = z
  .object({
    agentId: AgentIdSchema,
    displayName: z.string().min(1).max(120),
    /** Omitted: the creator. A group needs its manager or an administrator (PRD-26 D-19, D-41). */
    owner: OwnerRefSchema.optional()
  })
  .strict()

/** Moving an agent to another owner (PRD-26 D-32). Never changes who can use it. */
export const MoveAgentRequestSchema = z.object({ to: OwnerRefSchema }).strict()

/**
 * Lint findings shown in the builder; `blocking` ones stop publish (AGM-024).
 *
 * `legacy_setting_removed` is the odd one out: it reports on the RESTORE path rather than on
 * prose, and it is never blocking. Bodies written before the policy plane existed carry
 * settings the schema no longer accepts (per-agent retrieval tuning, the dead `humanInLoop`
 * block), and restoring one has to drop them. AGM-104 forbids dropping anything silently, and
 * the `dropped` list means something narrower — attachments this person may no longer hold —
 * so a legacy key would be miscategorised there. It is an informational finding instead: "we
 * restored this, minus two settings that are now administered centrally."
 */
export const LintFindingSchema = z.object({
  code: z.enum([
    'instructions_empty',
    'instructions_short',
    'contradicts_platform',
    'impersonation',
    'trust_mark',
    'reserved_name',
    'legacy_setting_removed'
  ]),
  field: z.string(),
  message: z.string(),
  /**
   * Which sentence this is (PRD-27 D-4). Neither `code` nor `code` plus `field` can say it:
   * one `contradicts_platform` covers three different accusations, and `trust_mark` says four
   * different things depending on which field carried the mark.
   *
   * Nullish for one producer only — the restore route assembles `legacy_setting_removed` from
   * the names of the settings it dropped, and that one still falls back to its English prose.
   * Everything `lintListingProse` returns carries a key.
   */
  messageKey: z.string().nullish(),
  /** What the sentence interpolates. Values only — never a pre-built phrase (D-20). */
  params: MessageParamsSchema.nullish(),
  blocking: z.boolean()
})
export type LintFinding = z.infer<typeof LintFindingSchema>

/**
 * Publishing, and bringing a retired agent back, both choose who can use it (PRD-26 D-13, D-40).
 *
 * The body carries the acknowledgement twice over, and deliberately (PRD-27 D-7): `ackText` is
 * the evidence — what was on the maker's screen, word for word, in whatever language they read
 * it — and `ackKey`/`ackVersion`/`ackLocale`/`ackParams` are the record, which can be re-rendered
 * in either language and checked against the evidence. The four are optional so a tab loaded
 * before this release still publishes; `ackText` stays required, because the evidence is the half
 * that was never allowed to be missing.
 */
export const PublishRequestSchema = z
  .object({
    audience: AudienceInputSchema,
    /** Must be literally true: the UI may not pre-check it (AGM-041). */
    ack: z.literal(true),
    /** The exact disclosure sentence the maker saw, persisted verbatim. */
    ackText: z.string().min(1).max(ACK_TEXT_MAX),
    ...ackRecordFields
  })
  .strict()
export type PublishRequest = z.infer<typeof PublishRequestSchema>

/**
 * What a publication did (PRD-26 D-40): what could go live went live now (`published`), and what
 * needs an administrator became one sharing request (`request`). Either may be absent.
 */
export const PublishResponseSchema = z.object({
  published: z.boolean(),
  request: z
    .object({
      id: z.uuid(),
      requestedKind: z.enum(['groups', 'everyone']),
      groups: z.array(GroupRefSchema)
    })
    .nullable()
})
export type PublishResponse = z.infer<typeof PublishResponseSchema>

export const VersionRestoreResultSchema = z.object({
  definitionVersion: z.number().int(),
  /** Attachments current policy/catalogue no longer permits, dropped with names (AGM-104). */
  dropped: z.array(z.object({ kind: z.enum(['knowledge', 'capability']), name: z.string() })),
  lint: z.array(LintFindingSchema)
})
export type VersionRestoreResult = z.infer<typeof VersionRestoreResultSchema>

// ---------------------------------------------------------------------------
// Admin surface
// ---------------------------------------------------------------------------

export const SuspendRequestSchema = z
  .object({
    /** Consumer-safe wording, shown on the listing and in conversations (AGM-047). */
    reasonPublic: z.string().min(1).max(500),
    /** The full story, owner + audit only. */
    reasonFull: z.string().min(1).max(4000),
    replacementAgentId: z.string().nullable().default(null)
  })
  .strict()

export const MarketplaceSettingsSchema = z.object({
  categories: z
    .array(z.string().min(1).max(60))
    .default(['General', 'HR & People', 'Finance', 'IT & Support', 'Sales', 'Operations']),
  /**
   * What one person, and one group, may hold (PRD-26 D-41).
   *
   * Personal limits bound what anybody can create on their own; group limits are larger because
   * a group's managers were appointed by an administrator and a team library outgrows a person's.
   * An administrator's own bases are unlimited. Review thresholds, sensitive groups and creation
   * groups used to live here; the standing rule (PRD-26 D-13) replaced the first two, and everyone
   * may build (D-22). A stored document that still carries them is read with them ignored.
   */
  quotas: z.object({
    agentsPerMaker: z.number().int().positive().default(10),
    basesPerMaker: z.number().int().positive().default(5),
    documentsPerBase: z.number().int().positive().default(100),
    storagePerBaseBytes: z.number().int().positive().default(262_144_000),
    agentsPerGroup: z.number().int().positive().default(25),
    basesPerGroup: z.number().int().positive().default(25),
    documentsPerGroupBase: z.number().int().positive().default(5_000),
    storagePerGroupBaseBytes: z.number().int().positive().default(5_368_709_120),
    previewRunsPerMakerPerDay: z.number().int().positive().default(50)
  }),
  archivedNameHoldDays: z.number().int().nonnegative().default(90)
})
export type MarketplaceSettings = z.infer<typeof MarketplaceSettingsSchema>

// ---------------------------------------------------------------------------
// Notifications (AGM-049)
// ---------------------------------------------------------------------------

export const NotificationSchema = z.object({
  id: z.uuid(),
  kind: z.enum([
    'health_changed',
    'review_requested',
    'review_decided',
    'suspended',
    'needs_owner',
    'editor_invited',
    'ownership_transferred',
    'copy_published_broader',
    'share_requested',
    // PRD-21: what happens to a person's own access. A deny is delivered as `role_revoked` —
    // the distinction between "the platform took it back" and "the platform is subtracting
    // what your directory sends" matters to an administrator reading a profile and not at all
    // to the person being told.
    'role_granted',
    'role_revoked',
    // PRD-26 D-31: transactional — somebody has to act, or somebody's ask was answered — plus
    // the one informative kind the product owner chose (a group's managers are told what was
    // shared into it). `review_requested`, `review_decided` and `share_requested` stay in the
    // enum only so an inbox that already holds them keeps reading; nothing writes them now.
    'sharing_requested',
    'sharing_decided',
    'lending_requested',
    'lending_decided',
    'shared_into_group'
  ]),
  /**
   * The English sentence, written when the event happened. Permanent, and never removed.
   *
   * It is the operator's line, the answer for anything that is not this web client, and the
   * record for every row written before `0014` — which cannot be retrofitted, because nothing
   * knows what those sentences were made of (PRD-27 D-5 amended).
   */
  subject: z.string(),
  body: z.string(),
  /**
   * The same two sentences as identifiers, so the reader's browser renders them in the
   * reader's language rather than in the language whoever triggered the event was using
   * (PRD-27 LOC-035, F-26 — there is no email transport, so every row is read back here).
   *
   * Nullish for the same reason `ApiIssue.messageKey` is: a row written by a pod that knows a
   * key is read by a client that does not, and a row written before `0014` has no key at all.
   * Both fall back to the prose above, which is why the prose stays.
   *
   * A plain string rather than an enum of `MessageKey`, also for that reason — strictness
   * belongs at the two ends (typed at the `notify()` call site, exhaustive in the catalogue),
   * not in the middle where it would turn a deploy-order problem into an outage.
   */
  subjectKey: z.string().nullish(),
  /** What the sentence interpolates. Values only — never a pre-built phrase (D-20). */
  subjectParams: MessageParamsSchema.nullish(),
  bodyKey: z.string().nullish(),
  bodyParams: MessageParamsSchema.nullish(),
  resourceType: z.string(),
  resourceId: z.string(),
  readAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime()
})
export type Notification = z.infer<typeof NotificationSchema>

export const NotificationListSchema = z.object({
  items: z.array(NotificationSchema),
  unread: z.number().int().nonnegative()
})
export type NotificationList = z.infer<typeof NotificationListSchema>
